import React, { useEffect, useState } from 'react'
import { ActivityIndicator, StyleSheet, Text, TextInput, TouchableOpacity, View } from 'react-native'
import { useSelector } from 'react-redux'

import CustomTextInput3 from '../../../../Feeds/CommentsTextInput/CustomTextInput3'
import Button from '../../../../UIControls/Button'
import Switch from '../../../../UIControls/Switch'
import styles, { colors } from '../../../../styles/global'
import { translate } from '../../../../../i18n/TranslationService'
import { PLAN_STATUS_PREMIUM } from '../../../../Premium/PremiumHelper'
import { NEW_TOPIC_MODAL_THEME } from '../../../../Feeds/CommentsTextInput/textInputHelper'
import { SELECTABLE_ASSISTANT_MODELS } from '../../../../../functions/Assistant/selectableAssistantModels'
import AssistantModelGoldRate from '../../../../UIComponents/AssistantModelGoldRate'
import {
    getGmailLabelingConfig,
    runGmailLabelingSync,
    saveGmailLabelingConfig,
} from '../../../../../utils/backends/Gmail/gmailLabelingFirestore'
import {
    GMAIL_LABELING_PROMPT_MODE_CUSTOM,
    GMAIL_LABELING_PROMPT_MODE_DEFAULT,
    buildCustomDefaultsForReset,
    buildDefaultConfigPreviewFromProjects,
    buildFullClassifierPromptPreview,
    createEmptyLabel,
    formatPostLabelActionStatus,
    normalizeConfig,
    sanitizeConfigForSave,
} from './GmailLabelingSettings.helpers'

const DIRECTION_OPTIONS = [
    { key: 'incoming', label: 'Incoming' },
    { key: 'outgoing', label: 'Outgoing' },
    { key: 'both', label: 'Both' },
]

function normalizeSyncDate(value) {
    if (!value) return null
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value
    if (typeof value?.toDate === 'function') {
        const date = value.toDate()
        return Number.isNaN(date?.getTime?.()) ? null : date
    }
    if (typeof value?.seconds === 'number') {
        const date = new Date(value.seconds * 1000)
        return Number.isNaN(date.getTime()) ? null : date
    }
    if (typeof value?._seconds === 'number') {
        const date = new Date(value._seconds * 1000)
        return Number.isNaN(date.getTime()) ? null : date
    }
    if (typeof value === 'number') {
        const date = new Date(value)
        return Number.isNaN(date.getTime()) ? null : date
    }
    if (typeof value === 'string') {
        const date = new Date(value)
        return Number.isNaN(date.getTime()) ? null : date
    }
    return null
}

function formatSyncDateTime(value) {
    const date = normalizeSyncDate(value)
    if (!date) return ''

    return new Intl.DateTimeFormat(undefined, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    }).format(date)
}

function stopEnterPropagation(event) {
    const key = event?.nativeEvent?.key || event?.key
    if (key === 'Enter') {
        event?.stopPropagation?.()
    }
}

function SyncSummary({ state, result }) {
    const lastError = result?.lastError || state?.lastError
    const syncDateLabel = formatSyncDateTime(result?.lastSyncAt || state?.lastSyncAt || state?.lastSuccessfulSyncAt)
    const lastClassifiedCount = typeof result?.classified === 'number' ? result.classified : null
    const lastLabeledCount =
        typeof result?.labeled === 'number'
            ? result.labeled
            : typeof state?.lastLabeledCount === 'number'
              ? state.lastLabeledCount
              : 0
    const lastArchivedCount =
        typeof result?.archived === 'number'
            ? result.archived
            : typeof state?.lastArchivedCount === 'number'
              ? state.lastArchivedCount
              : 0
    const lastProcessedCount =
        typeof result?.scanned === 'number'
            ? result.scanned
            : typeof state?.lastProcessedCount === 'number'
              ? state.lastProcessedCount
              : 0
    const goldSpent = typeof result?.goldSpent === 'number' ? result.goldSpent : 0
    const estimatedNormalGoldSpent =
        typeof result?.estimatedNormalGoldSpent === 'number' ? result.estimatedNormalGoldSpent : 0
    const hasManualSyncResult = !!result

    return (
        <View style={localStyles.summaryCard}>
            <Text style={localStyles.sectionTitle}>{translate('Gmail sync status')}</Text>
            {syncDateLabel ? (
                <Text style={localStyles.summaryText}>
                    {translate('Gmail sync last updated', { date: syncDateLabel })}
                </Text>
            ) : null}
            <Text style={localStyles.summaryText}>
                {translate('Gmail sync scanned', { count: lastProcessedCount })}
            </Text>
            {lastClassifiedCount !== null ? (
                <Text style={localStyles.summaryText}>
                    {translate('Gmail sync checked', { count: lastClassifiedCount })}
                </Text>
            ) : null}
            <Text style={localStyles.summaryText}>{translate('Gmail sync labeled', { count: lastLabeledCount })}</Text>
            <Text style={localStyles.summaryText}>
                {translate('Gmail sync archived', { count: lastArchivedCount })}
            </Text>
            {hasManualSyncResult ? (
                <Text style={localStyles.summaryText}>{translate('Gmail sync gold spent', { count: goldSpent })}</Text>
            ) : null}
            {hasManualSyncResult ? (
                <Text
                    style={localStyles.summaryText}
                >{`Normal token-based gold cost: ${estimatedNormalGoldSpent}`}</Text>
            ) : null}
            {state?.status ? (
                <Text style={localStyles.summaryText}>{translate('Gmail sync state', { state: state.status })}</Text>
            ) : null}
            {lastError ? <Text style={localStyles.errorText}>{lastError}</Text> : null}
        </View>
    )
}

function formatClassificationLabel(entry = {}) {
    if (entry.selectedGmailLabelName) return entry.selectedGmailLabelName
    if (entry.skippedReason === 'no_match') return 'No match'
    if (entry.skippedReason === 'missing_label_definition') return 'Missing label definition'
    if (entry.skippedReason === 'insufficient_gold') return 'Skipped: insufficient gold'
    if (entry.skippedReason === 'processing_error') return 'Processing error'
    return 'Skipped'
}

function formatDirectionLabel(direction = '') {
    if (direction === 'outgoing') return 'Outgoing'
    if (direction === 'both') return 'Both'
    return 'Incoming'
}

function formatReasoning(entry = {}) {
    const reasoning = typeof entry.reasoning === 'string' ? entry.reasoning.trim() : ''
    return reasoning || 'No reasoning available.'
}

function SyncAuditSection({ entries }) {
    const [expandedIds, setExpandedIds] = useState({})

    if (!Array.isArray(entries) || entries.length === 0) return null

    const toggleExpanded = id => {
        setExpandedIds(currentState => ({
            ...currentState,
            [id]: !currentState[id],
        }))
    }

    return (
        <View style={localStyles.auditSection}>
            <Text style={localStyles.sectionTitle}>Latest 20 emails</Text>
            {entries.map((entry, index) => {
                const rowId = entry.id || entry.gmailMessageId || `audit-${index}`
                const isExpanded = !!expandedIds[rowId]
                const classificationLabel = formatClassificationLabel(entry)
                const processedAtLabel = formatSyncDateTime(entry.processedAt)
                return (
                    <View key={rowId} style={localStyles.auditCard}>
                        <TouchableOpacity onPress={() => toggleExpanded(rowId)} activeOpacity={0.8}>
                            <Text style={localStyles.auditSubject}>{entry.subject || '(No subject)'}</Text>
                            <Text style={localStyles.auditMeta}>{entry.from || 'Unknown sender'}</Text>
                            <Text style={localStyles.auditMeta}>{`Direction: ${formatDirectionLabel(
                                entry.direction
                            )}`}</Text>
                            <Text
                                style={localStyles.auditClassification}
                            >{`Classification: ${classificationLabel}`}</Text>
                            {processedAtLabel ? (
                                <Text style={localStyles.auditTimestamp}>
                                    {translate('Gmail email classified at', { date: processedAtLabel })}
                                </Text>
                            ) : null}
                            <Text style={localStyles.auditToggle}>{isExpanded ? 'Hide details' : 'Show details'}</Text>
                        </TouchableOpacity>
                        {isExpanded ? (
                            <View style={localStyles.auditDetails}>
                                <Text style={localStyles.auditDetailText}>{`Why: ${formatReasoning(entry)}`}</Text>
                                {entry.snippet ? (
                                    <Text style={localStyles.auditDetailText}>{`Snippet: ${entry.snippet}`}</Text>
                                ) : null}
                                {typeof entry.confidence === 'number' ? (
                                    <Text style={localStyles.auditDetailText}>{`Confidence: ${entry.confidence}`}</Text>
                                ) : null}
                                {entry.processedAt ? (
                                    <Text style={localStyles.auditDetailText}>
                                        {`Processed: ${formatSyncDateTime(entry.processedAt)}`}
                                    </Text>
                                ) : null}
                                {entry.postLabelAction?.status ? (
                                    <Text style={localStyles.auditDetailText}>
                                        {`Follow-up: ${formatPostLabelActionStatus(entry.postLabelAction)}`}
                                    </Text>
                                ) : null}
                                {Array.isArray(entry.postLabelAction?.executedToolNames) &&
                                entry.postLabelAction.executedToolNames.length > 0 ? (
                                    <Text style={localStyles.auditDetailText}>
                                        {`Tools used: ${entry.postLabelAction.executedToolNames.join(', ')}`}
                                    </Text>
                                ) : null}
                                {entry.postLabelAction?.assistantResponse ? (
                                    <Text style={localStyles.auditDetailText}>
                                        {`Result: ${entry.postLabelAction.assistantResponse}`}
                                    </Text>
                                ) : null}
                                {entry.postLabelAction?.error ? (
                                    <Text style={localStyles.auditDetailText}>
                                        {`Follow-up error: ${entry.postLabelAction.error}`}
                                    </Text>
                                ) : null}
                            </View>
                        ) : null}
                    </View>
                )
            })}
        </View>
    )
}

function PromptModeSegment({ value, disabled, onChange }) {
    const options = [
        { key: GMAIL_LABELING_PROMPT_MODE_DEFAULT, label: 'Default' },
        { key: GMAIL_LABELING_PROMPT_MODE_CUSTOM, label: 'Custom' },
    ]

    return (
        <View style={localStyles.modeSegment}>
            {options.map(option => {
                const active = value === option.key
                return (
                    <TouchableOpacity
                        key={option.key}
                        style={[localStyles.modeButton, active && localStyles.modeButtonActive]}
                        onPress={() => onChange(option.key)}
                        disabled={disabled}
                        activeOpacity={0.8}
                    >
                        <Text style={[localStyles.modeButtonText, active && localStyles.modeButtonTextActive]}>
                            {option.label}
                        </Text>
                    </TouchableOpacity>
                )
            })}
        </View>
    )
}

function ModelSelector({ value, disabled, onChange }) {
    return (
        <View style={localStyles.modelOptions}>
            {SELECTABLE_ASSISTANT_MODELS.map(option => {
                const active = value === option.model
                return (
                    <TouchableOpacity
                        key={option.model}
                        style={[localStyles.modelOption, active && localStyles.modelOptionActive]}
                        onPress={() => onChange(option.model)}
                        disabled={disabled}
                        activeOpacity={0.8}
                    >
                        <Text style={[localStyles.modelOptionName, active && localStyles.modelOptionNameActive]}>
                            {option.name}
                        </Text>
                        <Text style={localStyles.modelOptionDescription}>{translate(option.descriptionKey)}</Text>
                        <AssistantModelGoldRate tokensPerGold={option.tokensPerGold} />
                    </TouchableOpacity>
                )
            })}
        </View>
    )
}

function DefaultLabelingPreview({ preview }) {
    const labelDefinitions = Array.isArray(preview?.labelDefinitions) ? preview.labelDefinitions : []

    return (
        <View style={localStyles.section}>
            <Text style={localStyles.sectionTitle}>Generated default labels</Text>
            {labelDefinitions.length > 0 ? (
                labelDefinitions.map((label, index) => (
                    <View key={label.key || `${label.gmailLabelName}-${index}`} style={localStyles.previewLabelCard}>
                        <Text style={localStyles.previewLabelName}>{label.gmailLabelName}</Text>
                        <Text style={localStyles.helperText}>{label.description}</Text>
                        {label.postLabelPrompt ? (
                            <>
                                <Text style={localStyles.previewFollowUpTitle}>Default follow-up prompt</Text>
                                <Text style={localStyles.helperText}>{label.postLabelPrompt}</Text>
                            </>
                        ) : null}
                    </View>
                ))
            ) : (
                <View style={localStyles.warningCard}>
                    <Text style={localStyles.warningTitle}>No active projects found</Text>
                    <Text style={localStyles.helperText}>
                        Default Gmail labeling needs at least one active project to generate labels.
                    </Text>
                </View>
            )}
        </View>
    )
}

function PromptMessagePreview({ title, content }) {
    return (
        <View style={localStyles.promptMessageCard}>
            <Text style={localStyles.previewLabelName}>{title}</Text>
            <Text style={localStyles.promptPreviewText} selectable={true}>
                {content}
            </Text>
        </View>
    )
}

function FullClassificationPromptPreview({ sections }) {
    return (
        <View style={localStyles.section}>
            <Text style={localStyles.sectionTitle}>{translate('Full classification prompt')}</Text>
            <Text style={localStyles.helperText}>
                {translate(
                    'This is the complete prompt sent to the classifier. Email values are inserted when each message is processed.'
                )}
            </Text>
            <PromptMessagePreview title={translate('System message')} content={sections.systemPrompt} />
            <PromptMessagePreview title={translate('Configuration message')} content={sections.staticUserContent} />
            <PromptMessagePreview
                title={translate('Per-email message template')}
                content={sections.dynamicUserContent}
            />
        </View>
    )
}

export default function GmailLabelingSettings({
    projectId,
    isConnected,
    authStatus,
    onUnsavedChangesChange,
    onRegisterCloseHandlers,
}) {
    const premiumStatus = useSelector(state => state.loggedUser.premium.status)
    const userDescription = useSelector(state => state.loggedUser.extendedDescription || '')
    const loggedUserProjects = useSelector(state => state.loggedUserProjects)
    const [config, setConfig] = useState(() => normalizeConfig(projectId))
    const [defaultConfigPreview, setDefaultConfigPreview] = useState(() =>
        buildDefaultConfigPreviewFromProjects(loggedUserProjects, projectId)
    )
    const [syncState, setSyncState] = useState(null)
    const [syncResult, setSyncResult] = useState(null)
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [syncing, setSyncing] = useState(false)
    const [error, setError] = useState('')
    const [successMessage, setSuccessMessage] = useState('')
    const [customDefaultsResetVersion, setCustomDefaultsResetVersion] = useState(0)
    const [savedConfigSnapshot, setSavedConfigSnapshot] = useState(null)
    const [initialLoadComplete, setInitialLoadComplete] = useState(false)
    const [recentAuditEntries, setRecentAuditEntries] = useState([])
    // Grow the learned-rules box to fit its content so every rule is visible without scrolling
    // inside a cramped single line; clamped to a sane range in the style below.
    const [learnedRulesHeight, setLearnedRulesHeight] = useState(0)

    const connectedEmail = authStatus?.email || config.gmailEmail || ''
    const isPremiumUser = premiumStatus === PLAN_STATUS_PREMIUM
    const needsReconnect = isConnected && authStatus?.hasCredentials && authStatus?.hasModifyScope === false
    const canManage = isPremiumUser && isConnected && authStatus?.hasCredentials && authStatus?.hasModifyScope !== false
    const currentConfigSnapshot = sanitizeConfigForSave(config)
    const hasUnsavedChanges =
        !!savedConfigSnapshot && JSON.stringify(currentConfigSnapshot) !== JSON.stringify(savedConfigSnapshot)

    useEffect(() => {
        if (typeof onUnsavedChangesChange === 'function') onUnsavedChangesChange(hasUnsavedChanges)
    }, [hasUnsavedChanges, onUnsavedChangesChange])

    useEffect(() => {
        let isMounted = true

        const loadConfig = async () => {
            if (!isPremiumUser || !isConnected || !authStatus?.hasCredentials) {
                setInitialLoadComplete(true)
                return
            }

            setInitialLoadComplete(false)
            setLoading(true)
            setError('')
            try {
                const result = await getGmailLabelingConfig(projectId)
                if (!isMounted) return

                const normalizedConfig = normalizeConfig(projectId, result?.config || {}, connectedEmail)
                setConfig(normalizedConfig)
                setDefaultConfigPreview(
                    result?.defaultConfigPreview || buildDefaultConfigPreviewFromProjects(loggedUserProjects, projectId)
                )
                setSyncState(result?.state || null)
                setRecentAuditEntries(Array.isArray(result?.recentAuditEntries) ? result.recentAuditEntries : [])
                setSavedConfigSnapshot(sanitizeConfigForSave(normalizedConfig))
            } catch (loadError) {
                if (!isMounted) return
                setError(loadError.message || translate('Failed to load Gmail labeling settings'))
            } finally {
                if (isMounted) {
                    setLoading(false)
                    setInitialLoadComplete(true)
                }
            }
        }

        loadConfig()

        return () => {
            isMounted = false
        }
    }, [
        projectId,
        isPremiumUser,
        isConnected,
        authStatus?.hasCredentials,
        authStatus?.hasModifyScope,
        connectedEmail,
        loggedUserProjects,
    ])

    useEffect(() => {
        const nextPreview = buildDefaultConfigPreviewFromProjects(loggedUserProjects, projectId)
        if (!defaultConfigPreview?.labelDefinitions?.length && nextPreview.labelDefinitions.length > 0) {
            setDefaultConfigPreview(nextPreview)
        }
    }, [defaultConfigPreview?.labelDefinitions?.length, loggedUserProjects])

    const updateConfig = patch => {
        setConfig(currentConfig => ({
            ...currentConfig,
            ...patch,
        }))
    }

    const updateLabel = (index, patch) => {
        setConfig(currentConfig => {
            const nextLabels = [...currentConfig.labelDefinitions]
            nextLabels[index] = {
                ...nextLabels[index],
                ...patch,
            }
            return {
                ...currentConfig,
                labelDefinitions: nextLabels,
            }
        })
    }

    const addLabel = () => {
        setConfig(currentConfig => ({
            ...currentConfig,
            labelDefinitions: [
                ...currentConfig.labelDefinitions,
                createEmptyLabel(currentConfig.labelDefinitions.length),
            ],
        }))
    }

    const removeLabel = index => {
        setConfig(currentConfig => ({
            ...currentConfig,
            labelDefinitions: currentConfig.labelDefinitions.filter((_, currentIndex) => currentIndex !== index),
        }))
    }

    const resetCustomDefaults = () => {
        const customDefaults = buildCustomDefaultsForReset()
        setConfig(currentConfig => ({
            ...currentConfig,
            promptMode: GMAIL_LABELING_PROMPT_MODE_CUSTOM,
            prompt: customDefaults.prompt,
            labelDefinitions: customDefaults.labelDefinitions,
        }))
        setCustomDefaultsResetVersion(currentVersion => currentVersion + 1)
    }

    const saveSettings = async () => {
        setSaving(true)
        setError('')
        setSuccessMessage('')

        try {
            const result = await saveGmailLabelingConfig(projectId, sanitizeConfigForSave(config))
            const normalizedConfig = normalizeConfig(
                projectId,
                result?.config || sanitizeConfigForSave(config),
                connectedEmail
            )
            setConfig(normalizedConfig)
            setSavedConfigSnapshot(sanitizeConfigForSave(normalizedConfig))
            setSuccessMessage(translate('Gmail labeling settings saved'))
            return true
        } catch (saveError) {
            setError(saveError.message || translate('Failed to save Gmail labeling settings'))
            return false
        } finally {
            setSaving(false)
        }
    }

    useEffect(() => {
        if (typeof onRegisterCloseHandlers !== 'function') return

        onRegisterCloseHandlers({
            saveAndClose: async () => await saveSettings(),
        })
    }, [config, connectedEmail, onRegisterCloseHandlers])

    const onSave = async () => {
        await saveSettings()
    }

    const onRunSync = async () => {
        setSyncing(true)
        setError('')
        setSuccessMessage('')

        try {
            const result = await runGmailLabelingSync(projectId, true)
            setSyncResult(result)
            setRecentAuditEntries(Array.isArray(result?.recentAuditEntries) ? result.recentAuditEntries : [])
            setSyncState(currentState => ({
                ...(currentState || {}),
                status: 'idle',
                lastError: result?.lastError || null,
                lastProcessedCount: result?.scanned || 0,
                lastLabeledCount: result?.labeled || 0,
                lastArchivedCount: result?.archived || 0,
            }))
            setSuccessMessage(translate('Gmail labeling sync completed'))
        } catch (syncError) {
            setError(syncError.message || translate('Failed to run Gmail labeling sync'))
        } finally {
            setSyncing(false)
        }
    }

    if (!isPremiumUser) {
        return (
            <View style={localStyles.warningCard}>
                <Text style={localStyles.warningTitle}>{translate('Premium required')}</Text>
                <Text style={localStyles.helperText}>{translate('GmailLabelingPremiumOnlyDescription')}</Text>
            </View>
        )
    }

    if (!isConnected) {
        return (
            <View style={localStyles.section}>
                <Text style={localStyles.helperText}>
                    {translate('Connect Gmail to configure prompt-based labeling')}
                </Text>
            </View>
        )
    }

    if (needsReconnect) {
        return (
            <View style={localStyles.warningCard}>
                <Text style={localStyles.warningTitle}>{translate('Reconnect required')}</Text>
                <Text style={localStyles.helperText}>{translate('GmailLabelingReconnectDescription')}</Text>
            </View>
        )
    }

    if (!authStatus?.hasCredentials) {
        return null
    }

    const showInitialLoadingState = !initialLoadComplete && !error
    const isDefaultPromptMode = config.promptMode === GMAIL_LABELING_PROMPT_MODE_DEFAULT
    const fullClassifierPrompt = buildFullClassifierPromptPreview({
        config,
        defaultConfigPreview,
        userDescription,
    })

    return (
        <View style={localStyles.container}>
            <View style={localStyles.section}>
                <View style={localStyles.headerRow}>
                    <Text style={[localStyles.sectionTitle, localStyles.rowText]}>{translate('Gmail labeling')}</Text>
                    <View style={localStyles.headerSwitchRow}>
                        <Switch
                            active={config.enabled}
                            activeSwitch={() => updateConfig({ enabled: true })}
                            deactiveSwitch={() => updateConfig({ enabled: false })}
                            disabled={!canManage}
                        />
                    </View>
                </View>
                <Text style={localStyles.helperText}>{translate('GmailLabelingDescription')}</Text>
                <Text style={localStyles.helperText}>{translate('GmailLabelingGoldCostDescription')}</Text>
                {connectedEmail ? (
                    <Text style={localStyles.summaryText}>
                        {translate('Connected Gmail account', { email: connectedEmail })}
                    </Text>
                ) : null}
            </View>

            {showInitialLoadingState ? (
                <View style={localStyles.loadingCard}>
                    <ActivityIndicator color={colors.Primary300} size="small" />
                    <Text style={localStyles.loadingText}>{translate('Loading Gmail labeling settings')}</Text>
                </View>
            ) : null}
            {error ? <Text style={localStyles.errorText}>{error}</Text> : null}
            {successMessage ? <Text style={localStyles.successText}>{successMessage}</Text> : null}

            {!showInitialLoadingState ? (
                <>
                    {config.enabled ? (
                        <>
                            <View style={localStyles.section}>
                                <View style={localStyles.switchRow}>
                                    <Text style={[localStyles.inputLabel, localStyles.rowText]}>
                                        {translate('Process unread only')}
                                    </Text>
                                    <Switch
                                        active={config.processUnreadOnly}
                                        activeSwitch={() => updateConfig({ processUnreadOnly: true })}
                                        deactiveSwitch={() => updateConfig({ processUnreadOnly: false })}
                                        disabled={!canManage}
                                    />
                                </View>

                                <View style={localStyles.switchRow}>
                                    <Text style={[localStyles.inputLabel, localStyles.rowText]}>
                                        {translate('Only inbox messages')}
                                    </Text>
                                    <Switch
                                        active={config.onlyInbox}
                                        activeSwitch={() => updateConfig({ onlyInbox: true })}
                                        deactiveSwitch={() => updateConfig({ onlyInbox: false })}
                                        disabled={!canManage}
                                    />
                                </View>

                                <View style={localStyles.switchRow}>
                                    <Text style={[localStyles.inputLabel, localStyles.rowText]}>
                                        {translate('Auto-archive all labeled emails')}
                                    </Text>
                                    <Switch
                                        active={config.autoArchiveAllLabeled}
                                        activeSwitch={() => updateConfig({ autoArchiveAllLabeled: true })}
                                        deactiveSwitch={() => updateConfig({ autoArchiveAllLabeled: false })}
                                        disabled={!canManage}
                                    />
                                </View>

                                <Text style={localStyles.inputLabel}>Labeling mode</Text>
                                <PromptModeSegment
                                    value={config.promptMode}
                                    disabled={!canManage}
                                    onChange={promptMode => updateConfig({ promptMode })}
                                />

                                <Text style={localStyles.inputLabel}>{translate('Gmail labeling model')}</Text>
                                <Text style={localStyles.helperText}>
                                    {translate('Choose the model used to classify and label emails.')}
                                </Text>
                                <ModelSelector
                                    value={config.model}
                                    disabled={!canManage}
                                    onChange={model => updateConfig({ model })}
                                />
                            </View>

                            {isDefaultPromptMode ? (
                                <>
                                    <View style={localStyles.section}>
                                        <View style={localStyles.switchRow}>
                                            <Text style={[localStyles.inputLabel, localStyles.rowText]}>
                                                {translate('Auto-archive Ads')}
                                            </Text>
                                            <Switch
                                                active={config.adsAutoArchive}
                                                activeSwitch={() => updateConfig({ adsAutoArchive: true })}
                                                deactiveSwitch={() => updateConfig({ adsAutoArchive: false })}
                                                disabled={!canManage}
                                            />
                                        </View>
                                        <Text style={localStyles.helperText}>
                                            {translate('AutoArchiveAdsDescription')}
                                        </Text>
                                    </View>
                                    <DefaultLabelingPreview preview={defaultConfigPreview} />
                                </>
                            ) : (
                                <>
                                    <View style={localStyles.section}>
                                        <View style={localStyles.sectionHeaderRow}>
                                            <Text style={[localStyles.inputLabel, localStyles.rowText]}>
                                                User prompt
                                            </Text>
                                            <Button
                                                title="Reset to defaults"
                                                type="ghost"
                                                onPress={resetCustomDefaults}
                                                disabled={!canManage}
                                                buttonStyle={localStyles.resetDefaultsButton}
                                            />
                                        </View>
                                        <CustomTextInput3
                                            containerStyle={[
                                                localStyles.input,
                                                localStyles.textArea,
                                                localStyles.multilineInput,
                                            ]}
                                            initialTextExtended={config.prompt}
                                            placeholder={translate(
                                                'Classify incoming Gmail messages into the configured labels'
                                            )}
                                            placeholderTextColor={colors.Text03}
                                            multiline={true}
                                            onChangeText={prompt => updateConfig({ prompt })}
                                            styleTheme={NEW_TOPIC_MODAL_THEME}
                                            disabledTabKey={true}
                                            disabledTags={true}
                                            disabledEdition={!canManage}
                                            externalTextStyle={localStyles.multilineInputText}
                                            keepBreakLines={true}
                                            allowPlainEnterBreakLines={true}
                                            onKeyPress={stopEnterPropagation}
                                            key={`gmail-prompt-${customDefaultsResetVersion}-${projectId}-${
                                                connectedEmail || 'default'
                                            }`}
                                        />
                                    </View>

                                    <View style={localStyles.section}>
                                        <Text style={localStyles.sectionTitle}>{translate('Rules')}</Text>
                                        {config.labelDefinitions.map((label, index) => (
                                            <View
                                                key={label.id || `${label.key}-${index}`}
                                                style={localStyles.labelCard}
                                            >
                                                <Text style={localStyles.inputLabel}>
                                                    {translate('Gmail rule number', { number: index + 1 })}
                                                </Text>
                                                <TextInput
                                                    value={label.gmailLabelName}
                                                    onChangeText={gmailLabelName =>
                                                        updateLabel(index, { gmailLabelName })
                                                    }
                                                    editable={canManage}
                                                    style={localStyles.input}
                                                    placeholder={translate('Gmail label name')}
                                                    placeholderTextColor={colors.Text03}
                                                />
                                                <Text style={localStyles.inputLabel}>Rule direction</Text>
                                                <View style={localStyles.directionRow}>
                                                    {DIRECTION_OPTIONS.map(option => {
                                                        const active =
                                                            (label.directionScope || 'incoming') === option.key
                                                        return (
                                                            <TouchableOpacity
                                                                key={option.key}
                                                                style={[
                                                                    localStyles.directionButton,
                                                                    active && localStyles.directionButtonActive,
                                                                ]}
                                                                onPress={() =>
                                                                    updateLabel(index, { directionScope: option.key })
                                                                }
                                                                disabled={!canManage}
                                                                activeOpacity={0.8}
                                                            >
                                                                <Text
                                                                    style={[
                                                                        localStyles.directionButtonText,
                                                                        active && localStyles.directionButtonTextActive,
                                                                    ]}
                                                                >
                                                                    {option.label}
                                                                </Text>
                                                            </TouchableOpacity>
                                                        )
                                                    })}
                                                </View>
                                                <CustomTextInput3
                                                    containerStyle={[
                                                        localStyles.input,
                                                        localStyles.descriptionInput,
                                                        localStyles.multilineInput,
                                                    ]}
                                                    initialTextExtended={label.description}
                                                    placeholder={translate('Describe when this label should be used')}
                                                    placeholderTextColor={colors.Text03}
                                                    multiline={true}
                                                    onChangeText={description => updateLabel(index, { description })}
                                                    styleTheme={NEW_TOPIC_MODAL_THEME}
                                                    disabledTabKey={true}
                                                    disabledTags={true}
                                                    disabledEdition={!canManage}
                                                    externalTextStyle={localStyles.multilineInputText}
                                                    keepBreakLines={true}
                                                    allowPlainEnterBreakLines={true}
                                                    onKeyPress={stopEnterPropagation}
                                                    key={`gmail-rule-${label.id || index}`}
                                                />
                                                <View style={localStyles.switchRow}>
                                                    <Text style={[localStyles.inputLabel, localStyles.rowText]}>
                                                        {translate('Auto-archive when matched')}
                                                    </Text>
                                                    <Switch
                                                        active={!!label.autoArchive}
                                                        activeSwitch={() => updateLabel(index, { autoArchive: true })}
                                                        deactiveSwitch={() =>
                                                            updateLabel(index, { autoArchive: false })
                                                        }
                                                        disabled={!canManage}
                                                    />
                                                </View>
                                                <Text style={localStyles.inputLabel}>
                                                    Follow-up prompt after labeling
                                                </Text>
                                                <CustomTextInput3
                                                    containerStyle={[
                                                        localStyles.input,
                                                        localStyles.postLabelPromptInput,
                                                        localStyles.multilineInput,
                                                    ]}
                                                    initialTextExtended={label.postLabelPrompt}
                                                    placeholder={
                                                        'Optional: run an assistant prompt after labeling, for example create_task actions.'
                                                    }
                                                    placeholderTextColor={colors.Text03}
                                                    multiline={true}
                                                    onChangeText={postLabelPrompt =>
                                                        updateLabel(index, { postLabelPrompt })
                                                    }
                                                    styleTheme={NEW_TOPIC_MODAL_THEME}
                                                    disabledTabKey={true}
                                                    disabledTags={true}
                                                    disabledEdition={!canManage}
                                                    externalTextStyle={localStyles.multilineInputText}
                                                    keepBreakLines={true}
                                                    allowPlainEnterBreakLines={true}
                                                    onKeyPress={stopEnterPropagation}
                                                    key={`gmail-rule-post-label-${label.id || index}`}
                                                />
                                                <Button
                                                    title={translate('Remove rule')}
                                                    type="ghost"
                                                    onPress={() => removeLabel(index)}
                                                    disabled={!canManage || config.labelDefinitions.length <= 1}
                                                    titleStyle={{ color: colors.UtilityRed200 }}
                                                    buttonStyle={{
                                                        alignSelf: 'flex-start',
                                                        borderColor: colors.UtilityRed200,
                                                        borderWidth: 1,
                                                    }}
                                                />
                                            </View>
                                        ))}
                                        <Button
                                            title={translate('Add rule')}
                                            type="ghost"
                                            onPress={addLabel}
                                            disabled={!canManage}
                                            buttonStyle={{ alignSelf: 'flex-start' }}
                                        />
                                    </View>
                                </>
                            )}

                            <View style={localStyles.section}>
                                <Text style={localStyles.inputLabel}>{translate('Gmail sync interval minutes')}</Text>
                                <TextInput
                                    value={config.syncIntervalMinutes}
                                    onChangeText={syncIntervalMinutes => updateConfig({ syncIntervalMinutes })}
                                    editable={canManage}
                                    style={localStyles.input}
                                    keyboardType="numeric"
                                    placeholder={'5'}
                                    placeholderTextColor={colors.Text03}
                                />
                                <Text style={localStyles.helperText}>
                                    {translate('Gmail sync interval description')}
                                </Text>
                                <Text style={localStyles.inputLabel}>{translate('Max messages per run')}</Text>
                                <TextInput
                                    value={config.maxMessagesPerRun}
                                    onChangeText={maxMessagesPerRun => updateConfig({ maxMessagesPerRun })}
                                    editable={canManage}
                                    style={localStyles.input}
                                    keyboardType="numeric"
                                    placeholder={translate('Gmail max messages placeholder')}
                                    placeholderTextColor={colors.Text03}
                                />
                                <Text style={localStyles.helperText}>{translate('GmailMaxMessagesDescription')}</Text>
                                <Text style={localStyles.inputLabel}>{translate('Gmail lookback days')}</Text>
                                <TextInput
                                    value={config.lookbackDays}
                                    onChangeText={lookbackDays => updateConfig({ lookbackDays })}
                                    editable={canManage}
                                    style={localStyles.input}
                                    keyboardType="numeric"
                                    placeholder={translate('Gmail lookback days placeholder')}
                                    placeholderTextColor={colors.Text03}
                                />
                                <Text style={localStyles.helperText}>{translate('GmailLookbackDaysDescription')}</Text>
                                <Text style={localStyles.inputLabel}>{translate('Confidence threshold')}</Text>
                                <TextInput
                                    value={config.confidenceThreshold}
                                    onChangeText={confidenceThreshold => updateConfig({ confidenceThreshold })}
                                    editable={canManage}
                                    style={localStyles.input}
                                    keyboardType="numeric"
                                    placeholder={translate('Gmail confidence threshold placeholder')}
                                    placeholderTextColor={colors.Text03}
                                />
                                <Text style={localStyles.helperText}>
                                    {translate('Gmail confidence threshold description')}
                                </Text>
                            </View>

                            <View style={localStyles.section}>
                                <Text style={localStyles.inputLabel}>
                                    {translate('Learned rules from your feedback')}
                                </Text>
                                <TextInput
                                    value={config.learnedRules}
                                    onChangeText={learnedRules => updateConfig({ learnedRules })}
                                    editable={canManage}
                                    style={[
                                        localStyles.input,
                                        localStyles.learnedRulesInput,
                                        learnedRulesHeight
                                            ? { height: Math.min(Math.max(learnedRulesHeight + 20, 120), 400) }
                                            : null,
                                    ]}
                                    multiline
                                    onContentSizeChange={event =>
                                        setLearnedRulesHeight(event.nativeEvent.contentSize.height)
                                    }
                                    placeholder={translate('LearnedRulesPlaceholder')}
                                    placeholderTextColor={colors.Text03}
                                />
                                <Text style={localStyles.helperText}>{translate('LearnedRulesDescription')}</Text>
                            </View>

                            <FullClassificationPromptPreview sections={fullClassifierPrompt} />

                            <SyncSummary state={syncState} result={syncResult} />
                            <SyncAuditSection entries={recentAuditEntries} />
                        </>
                    ) : null}

                    {hasUnsavedChanges ? (
                        <View style={localStyles.warningCard}>
                            <Text style={localStyles.warningTitle}>{translate('Unsaved changes')}</Text>
                            <Text style={localStyles.helperText}>
                                {translate('GmailLabelingUnsavedChangesDescription')}
                            </Text>
                        </View>
                    ) : null}

                    <View style={localStyles.buttonRow}>
                        <Button
                            title={translate('Save Gmail labeling settings')}
                            onPress={onSave}
                            disabled={!canManage || saving}
                            processing={saving}
                            processingTitle={translate('Saving')}
                            buttonStyle={{ marginRight: 12 }}
                        />
                        <Button
                            title={translate('Run Gmail sync now')}
                            type="ghost"
                            onPress={onRunSync}
                            disabled={!canManage || syncing || hasUnsavedChanges}
                            processing={syncing}
                            processingTitle={translate('Syncing')}
                        />
                    </View>
                </>
            ) : null}
        </View>
    )
}

const localStyles = StyleSheet.create({
    container: {
        marginTop: 16,
    },
    section: {
        marginBottom: 16,
    },
    sectionTitle: {
        ...styles.subtitle1,
        color: '#ffffff',
        marginBottom: 8,
    },
    sectionHeaderRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 6,
    },
    resetDefaultsButton: {
        alignSelf: 'flex-start',
        marginBottom: 4,
    },
    headerRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 8,
    },
    headerSwitchRow: {
        flexDirection: 'row',
        alignItems: 'center',
        marginLeft: 16,
    },
    helperText: {
        ...styles.body2,
        color: colors.Text03,
        lineHeight: 20,
    },
    summaryText: {
        ...styles.body2,
        color: '#ffffff',
        marginTop: 4,
    },
    loadingCard: {
        minHeight: 140,
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.08)',
        borderRadius: 8,
        backgroundColor: 'rgba(255,255,255,0.03)',
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: 16,
        paddingVertical: 24,
        marginBottom: 16,
    },
    loadingText: {
        ...styles.body2,
        color: colors.Text03,
        marginTop: 12,
        textAlign: 'center',
    },
    inputLabel: {
        ...styles.caption1,
        color: colors.Text03,
        marginBottom: 6,
    },
    input: {
        borderWidth: 1,
        borderColor: colors.Text03,
        borderRadius: 6,
        paddingHorizontal: 10,
        paddingVertical: 10,
        color: '#ffffff',
        marginBottom: 10,
        backgroundColor: 'rgba(255,255,255,0.04)',
    },
    textArea: {
        minHeight: 96,
        textAlignVertical: 'top',
    },
    descriptionInput: {
        minHeight: 104,
        paddingTop: 12,
        textAlignVertical: 'top',
    },
    postLabelPromptInput: {
        minHeight: 120,
        paddingTop: 12,
        textAlignVertical: 'top',
    },
    multilineInput: {
        paddingVertical: 3,
        paddingHorizontal: 16,
    },
    learnedRulesInput: {
        minHeight: 120,
        paddingTop: 12,
        textAlignVertical: 'top',
        lineHeight: 20,
    },
    multilineInputText: {
        ...styles.body2,
        color: '#ffffff',
    },
    switchRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 12,
    },
    rowText: {
        flex: 1,
        marginRight: 12,
    },
    labelCard: {
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.08)',
        borderRadius: 8,
        padding: 12,
        marginBottom: 12,
        backgroundColor: 'rgba(255,255,255,0.03)',
    },
    directionRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        marginBottom: 10,
    },
    directionButton: {
        paddingHorizontal: 10,
        paddingVertical: 8,
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.16)',
        borderRadius: 6,
        marginRight: 8,
        backgroundColor: 'rgba(255,255,255,0.02)',
    },
    directionButtonActive: {
        borderColor: colors.Primary300,
        backgroundColor: 'rgba(66, 153, 225, 0.18)',
    },
    directionButtonText: {
        ...styles.caption1,
        color: colors.Text03,
    },
    directionButtonTextActive: {
        color: '#ffffff',
    },
    modeSegment: {
        flexDirection: 'row',
        alignSelf: 'flex-start',
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.16)',
        borderRadius: 6,
        overflow: 'hidden',
        marginBottom: 10,
    },
    modeButton: {
        paddingHorizontal: 14,
        paddingVertical: 8,
        backgroundColor: 'rgba(255,255,255,0.02)',
    },
    modeButtonActive: {
        backgroundColor: 'rgba(66, 153, 225, 0.22)',
    },
    modeButtonText: {
        ...styles.caption1,
        color: colors.Text03,
    },
    modeButtonTextActive: {
        color: '#ffffff',
    },
    modelOptions: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        marginTop: 4,
        marginBottom: 10,
    },
    modelOption: {
        flexGrow: 1,
        flexBasis: 150,
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.16)',
        borderRadius: 6,
        paddingHorizontal: 12,
        paddingVertical: 10,
        marginRight: 8,
        marginTop: 8,
        backgroundColor: 'rgba(255,255,255,0.02)',
    },
    modelOptionActive: {
        borderColor: colors.Primary300,
        backgroundColor: 'rgba(66, 153, 225, 0.18)',
    },
    modelOptionName: {
        ...styles.subtitle2,
        color: '#ffffff',
    },
    modelOptionNameActive: {
        color: colors.Primary300,
    },
    modelOptionDescription: {
        ...styles.caption1,
        color: colors.Text03,
        marginTop: 4,
    },
    previewLabelCard: {
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.08)',
        borderRadius: 8,
        padding: 12,
        marginBottom: 10,
        backgroundColor: 'rgba(255,255,255,0.03)',
    },
    previewLabelName: {
        ...styles.subtitle2,
        color: '#ffffff',
        marginBottom: 6,
    },
    previewFollowUpTitle: {
        ...styles.caption1,
        color: colors.Text03,
        marginTop: 10,
        marginBottom: 4,
    },
    promptMessageCard: {
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.08)',
        borderRadius: 8,
        padding: 12,
        marginTop: 10,
        backgroundColor: 'rgba(255,255,255,0.03)',
    },
    promptPreviewText: {
        ...styles.body2,
        color: '#ffffff',
        lineHeight: 20,
    },
    buttonRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'center',
        marginTop: 4,
    },
    warningCard: {
        marginTop: 16,
        padding: 12,
        borderRadius: 8,
        backgroundColor: 'rgba(255, 196, 0, 0.12)',
        borderWidth: 1,
        borderColor: 'rgba(255, 196, 0, 0.35)',
    },
    warningTitle: {
        ...styles.subtitle2,
        color: colors.UtilityYellow200,
        marginBottom: 6,
    },
    summaryCard: {
        marginBottom: 16,
        padding: 12,
        borderRadius: 8,
        backgroundColor: 'rgba(255,255,255,0.03)',
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.08)',
    },
    auditSection: {
        marginBottom: 16,
    },
    auditCard: {
        marginBottom: 10,
        padding: 12,
        borderRadius: 8,
        backgroundColor: 'rgba(255,255,255,0.03)',
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.08)',
    },
    auditSubject: {
        ...styles.subtitle2,
        color: '#ffffff',
        marginBottom: 4,
    },
    auditMeta: {
        ...styles.caption1,
        color: colors.Text03,
        marginBottom: 4,
    },
    auditClassification: {
        ...styles.body2,
        color: '#ffffff',
    },
    auditTimestamp: {
        ...styles.caption1,
        color: colors.Text03,
        marginTop: 4,
    },
    auditToggle: {
        ...styles.caption1,
        color: colors.Primary300,
        marginTop: 6,
    },
    auditDetails: {
        marginTop: 10,
        paddingTop: 10,
        borderTopWidth: 1,
        borderTopColor: 'rgba(255,255,255,0.08)',
    },
    auditDetailText: {
        ...styles.body2,
        color: colors.Text03,
        marginBottom: 6,
        lineHeight: 20,
    },
    errorText: {
        ...styles.body2,
        color: colors.UtilityRed200,
        marginBottom: 8,
    },
    successText: {
        ...styles.body2,
        color: colors.Primary300,
        marginBottom: 8,
    },
})
